import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
// Night Mode fully removed per user road-test directive — map tiles stay
// pinned to a single high-contrast daylight style ("atlas") 100% of the
// time. The useNightMode hook is no longer imported here.

/**
 * MapLibre GL JS-based MapView. Replaces the legacy Leaflet renderer.
 *
 * Rationale for the pivot: after the road test, custom-Leaflet tracking was
 * unusable (arrow drifted into fields, no dynamic zoom, obtrusive off-route
 * banner). MapLibre gives us WebGL rendering + native easeTo/flyTo for
 * silent, industrial-grade follow behaviour equivalent to Mapbox GL JS
 * (same style spec, same API surface), with ZERO paid API key required.
 *
 * Snap-to-road: we snap the live GPS position onto the generated route
 * polyline client-side (see useTracking → snappedPos). MapLibre just
 * renders the marker at the snapped coord, so the arrow always sits on
 * the yellow line even when raw GPS drifts.
 *
 * Follow-user: while `followUser` is true we call `map.easeTo({center,
 * zoom, bearing})` on every position update; the pan animation is what
 * makes the driving experience feel smooth. We push the vehicle to the
 * lower-center of the viewport (0.65 vertical anchor) so the road ahead
 * fills the top 2/3 of the screen.
 */

const TF_ATTRIB =
  '© <a href="https://www.thunderforest.com/">Thunderforest</a> · © OSM contributors';
const CARTO_ATTRIB = '© <a href="https://carto.com/">CARTO</a> · © OSM';

function buildStyle(tfKey, tfStyleName) {
  const url = tfKey
    ? `https://{s}.tile.thunderforest.com/${tfStyleName}/{z}/{x}/{y}.png?apikey=${tfKey}`
    : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png";
  const attribution = tfKey ? TF_ATTRIB : CARTO_ATTRIB;
  return {
    version: 8,
    sources: {
      raster: {
        type: "raster",
        tiles: [
          url.replace("{s}", "a"),
          url.replace("{s}", "b"),
          url.replace("{s}", "c"),
        ],
        tileSize: 256,
        attribution,
        maxzoom: 19,
      },
    },
    layers: [
      {
        id: "bg",
        type: "background",
        paint: { "background-color": "#131822" },
      },
      {
        id: "raster",
        type: "raster",
        source: "raster",
        paint: { "raster-opacity": 1 },
      },
    ],
  };
}

// Yellow start dot marker
function startMarkerEl() {
  const el = document.createElement("div");
  el.style.cssText =
    "width:18px;height:18px;border-radius:999px;background:#FFD400;border:3px solid #131822;box-shadow:0 0 0 2px #FFD400, 0 0 12px #FFD400";
  return el;
}

// Live arrow marker (rotates via CSS transform on inner div)
function liveMarkerEl() {
  const el = document.createElement("div");
  el.className = "brema-live-marker";
  el.innerHTML = `
    <div class="brema-live-pulse"></div>
    <div class="brema-live-arrow">
      <svg viewBox="0 0 24 24" width="100%" height="100%">
        <circle cx="12" cy="12" r="11" fill="#131822" stroke="#C1272D" stroke-width="2"/>
        <path d="M12 3 L17 18 L12 14 L7 18 Z" fill="#C1272D" stroke="#FFF" stroke-width="0.8" stroke-linejoin="round"/>
      </svg>
    </div>`;
  return el;
}

// Turn point flag (yellow) with attached label chip
function turnMarkerEl(label) {
  const safe = (label || "").replace(/</g, "&lt;");
  const el = document.createElement("div");
  el.style.cssText = "position:relative;width:1px;height:1px;";
  el.innerHTML = `
    <div style="position:absolute;left:-13px;top:-30px;width:26px;height:30px;">
      <svg viewBox="0 0 26 30" width="26" height="30">
        <line x1="4" y1="30" x2="4" y2="2" stroke="#131822" stroke-width="2"/>
        <path d="M4 2 L22 6 L4 12 Z" fill="#FFD400" stroke="#131822" stroke-width="1.5" stroke-linejoin="round"/>
      </svg>
    </div>
    <div style="position:absolute;left:14px;top:-24px;background:#FFD400;color:#131822;padding:3px 7px;font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;white-space:nowrap;border:1px solid #131822;box-shadow:0 2px 6px rgba(0,0,0,0.4);">${safe}</div>`;
  return el;
}

// (Weather badge markers removed per user directive after real-world road test:
// the orange temperature pills cluttered the map and hid the roads. Weather
// data remains fetched in the background for the elevation card, but it is
// NEVER overlaid on the map surface.)

export default function MapView({
  start,
  polyline,
  onPickStart,
  live,
  followUser,
  compassMode,
  nearTurn,
  snapHeading,
  rejoinPolyline,
  turnPoint,
  // eslint-disable-next-line no-unused-vars
  weatherPoints,
  speedKmh,
  onRequestRecenter,
  userPannedSignal,
}) {
  const tfKey = process.env.REACT_APP_THUNDERFOREST_API_KEY;
  // NIGHT MODE DISABLED per user directive after road test:
  //   "Forzate la mappa su un unico tema chiaro/daylight ad alto contrasto
  //   fissa al 100%." — pinned to Thunderforest "atlas" (clean high-
  //   contrast street map) so the yellow polyline always cuts through.
  const tfStyleName = "atlas";

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const startMarkerRef = useRef(null);
  const liveMarkerRef = useRef(null);
  const turnMarkerRef = useRef(null);
  const didFitOnceRef = useRef(false);

  // Latest polyline + rejoin data, always accessible from event callbacks
  // (setStyle re-init, sourcedataloading retries, etc). React state deps
  // alone are not enough because setStyle re-adds sources *after* the
  // effect has closed over a stale value.
  const polylineRef = useRef(polyline);
  const rejoinRef = useRef(rejoinPolyline);
  useEffect(() => { polylineRef.current = polyline; }, [polyline]);
  useEffect(() => { rejoinRef.current = rejoinPolyline; }, [rejoinPolyline]);

  // Centralised "apply route/rejoin data to sources" — safe to call any
  // time, guards against missing source / missing map. Also re-lifts the
  // route layers to the very top of the stack every time data changes,
  // as an extra safety net against MapLibre re-inserting a raster/label
  // layer above them during tile loading.
  const applyRouteData = (map) => {
    if (!map) return;
    const pl = polylineRef.current;
    const rj = rejoinRef.current;
    const routeSrc = map.getSource("route");
    if (routeSrc) {
      if (pl && pl.length > 1) {
        routeSrc.setData({
          type: "Feature",
          geometry: { type: "LineString", coordinates: pl.map((p) => [p[1], p[0]]) },
        });
      } else {
        routeSrc.setData({ type: "FeatureCollection", features: [] });
      }
    }
    const rejoinSrc = map.getSource("rejoin");
    if (rejoinSrc) {
      if (rj && rj.length > 1) {
        rejoinSrc.setData({
          type: "Feature",
          geometry: { type: "LineString", coordinates: rj.map((p) => [p[1], p[0]]) },
        });
      } else {
        rejoinSrc.setData({ type: "FeatureCollection", features: [] });
      }
    }
    // Force route layers to the TOP after every data change (paranoia
    // against layer re-ordering by MapLibre internals).
    for (const id of ["route-shadow", "route-line", "rejoin-shadow", "rejoin-line"]) {
      try {
        if (map.getLayer(id)) map.moveLayer(id);
      } catch { /* ignore */ }
    }
    // Force a repaint. On some iOS Safari builds, setData on a geojson
    // source doesn't schedule a repaint automatically until the next user
    // interaction.
    try { map.triggerRepaint(); } catch { /* ignore */ }
    // Also invalidate the canvas overlay so it re-draws with fresh data.
    try { drawCanvasOverlayRef.current?.(); } catch { /* ignore */ }
  };

  // ---- Init map ----
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildStyle(tfKey, tfStyleName),
      center: start ? [start.lng, start.lat] : [9.85, 45.85],
      zoom: 11,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      preserveDrawingBuffer: true,
      // Default is 3px — on a vehicle-mounted phone, engine/road vibration
      // alone exceeds that and fires a real dragstart/zoomstart, which the
      // "user panned" detector below (dragstart/zoomstart/rotatestart
      // listener) can't tell apart from an intentional gesture. That falsely
      // disables camera auto-follow for 15s at a time, re-armed by every
      // subsequent vibration — net effect: follow never resumes on a bumpy
      // ride. 20px absorbs vibration jitter while staying far below any
      // real intentional pan/pinch distance.
      clickTolerance: 20,
    });
    map.on("error", (e) => {
      // Suppress transient tile-fetch failures — Thunderforest rate-limits
      // some CI/proxy origins and MapLibre auto-retries on the next pan.
      // Real code faults (missing sources, invalid style) still surface.
      const msg = e?.error?.message || String(e || "");
      if (/Failed to fetch|NetworkError|AbortError/i.test(msg)) return;
      // eslint-disable-next-line no-console
      console.error("[MapView] maplibre:", msg);
    });
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    // "style.load" fires as soon as the style JSON is parsed — reliable
    // even in headless envs where raster tile loads are delayed. "load"
    // waits for *tiles* which sometimes never resolves under CI WebGL.
    const onStyleReady = () => {
      if (map.getSource("route")) return;      // Route line: black shadow underneath + yellow line on top for the
      // rally look. Two-layer trick same as the old Leaflet impl.
      map.addSource("route", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "route-shadow",
        type: "line",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#000000",
          "line-width": 12,
          "line-opacity": 1,
        },
      });
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#FFEE00",
          "line-width": 8,
          "line-opacity": 1,
        },
      });

      // Rejoin polyline (silent recalculation): dashed yellow "back-to-track"
      // — no obtrusive banner, just visual guidance.
      map.addSource("rejoin", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "rejoin-shadow",
        type: "line",
        source: "rejoin",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#000000",
          "line-width": 10,
          "line-opacity": 0.6,
        },
      });
      map.addLayer({
        id: "rejoin-line",
        type: "line",
        source: "rejoin",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          // Phase 5.3 user directive: NO dashed/dotted styles anywhere on
          // the active route — the yellow line MUST be a single solid,
          // uniform stroke from start to end. The rejoin overlay is now
          // rendered as a plain yellow line (same width as the route)
          // and is only kept as a defensive safety net; the normal path
          // now REPLACES the master polyline via onRerouted, so this
          // overlay usually stays empty during tracking.
          "line-color": "#FFEE00",
          "line-width": 8,
          "line-opacity": 1,
        },
      });
      // Force route + rejoin layers to the very TOP of the layer stack.
      try {
        map.moveLayer("route-shadow");
        map.moveLayer("route-line");
        map.moveLayer("rejoin-shadow");
        map.moveLayer("rejoin-line");
      } catch { /* ignore — layers were just added */ }
      setMapReady(true);
      // Apply any polyline data that arrived BEFORE the style finished
      // loading (common on re-mount during tracking start).
      applyRouteData(map);
      // Force an immediate repaint. Under headless Chromium (SwiftShader)
      // MapLibre sometimes skips the first paint until an interaction.
      map.triggerRepaint();
    };
    map.on("style.load", onStyleReady);
    // Fallback: `load` event as an additional trigger. Whichever fires
    // first will set up the sources; guard against double-init inside
    // onStyleReady via getSource("route") check.
    map.on("load", onStyleReady);
    // Extra safety net: whenever the raster tile source finishes loading
    // a batch of tiles, re-apply route data. Guards against the rare
    // MapLibre codepath where a mid-tile-load re-flush clears our
    // geojson source silently (observed on iOS Safari during pan while
    // tracking).
    map.on("sourcedata", (e) => {
      if (e.sourceId === "route" || e.sourceId === "rejoin") return; // avoid recursion
      applyRouteData(map);
    });

    map.on("click", (e) => {
      if (onPickStart) onPickStart(e.lngLat.lat, e.lngLat.lng);
    });

    mapRef.current = map;
    // If the container's height was 0 at construction time (e.g. inside an
    // absolutely-positioned wrapper that hadn't been laid out yet), MapLibre
    // skips tile requests entirely. Force a resize after the browser has
    // painted, and again after 300ms as a safety net.
    requestAnimationFrame(() => {
      try { map.resize(); } catch { /* ignore */ }
    });
    setTimeout(() => {
      try { map.resize(); } catch { /* ignore */ }
    }, 300);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Detect manual user pan/zoom during tracking ----
  // When the rider drags the map with a finger to peek ahead, we want to
  // PAUSE the auto-follow until they tap "RICENTRA" (or 15s of inactivity
  // pass, handled in the Dashboard parent). MapLibre fires `dragstart`
  // ONLY on user-initiated drags; programmatic `easeTo` calls don't
  // trigger it. Same for `zoomstart` when the user pinches.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const onUserPan = (e) => {
      // originalEvent is present on user-initiated events, absent on
      // programmatic map.easeTo() calls.
      if (e?.originalEvent && onRequestRecenter) {
        onRequestRecenter();
      }
    };
    map.on("dragstart", onUserPan);
    map.on("zoomstart", onUserPan);
    map.on("rotatestart", onUserPan);
    return () => {
      map.off("dragstart", onUserPan);
      map.off("zoomstart", onUserPan);
      map.off("rotatestart", onUserPan);
    };
  }, [mapReady, onRequestRecenter]);

  // ---- Style swap effect — GUTTED per user directive.
  //      Night Mode is disabled: tfStyleName is now a constant "atlas".
  //      This effect used to swap tiles between "outdoors" (day) and
  //      "transport-dark" (night), but that mid-ride swap was confusing
  //      and hid the polyline behind fresh raster tiles until a new
  //      styledata event finished. Keeping the constant tfStyleName pins
  //      the tile source once at init — no more setStyle calls, no more
  //      layer re-adds, and no more chance for the route line to land
  //      under the raster layer. ----

  // ---- Route polyline updates ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Always apply — applyRouteData is safe against missing source (early
    // during style.load) and handles both set/clear.
    applyRouteData(map);
    // Fit once per route (when tracking is off; while tracking the follow
    // logic owns the camera).
    if (mapReady && polyline && polyline.length > 1 && !followUser && !didFitOnceRef.current) {
      const bounds = polyline.reduce(
        (b, p) => b.extend([p[1], p[0]]),
        new maplibregl.LngLatBounds([polyline[0][1], polyline[0][0]], [polyline[0][1], polyline[0][0]])
      );
      map.fitBounds(bounds, { padding: 60, duration: 700 });
      didFitOnceRef.current = true;
    }
    if (!polyline || polyline.length <= 1) {
      didFitOnceRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polyline, mapReady, followUser]);

  // When the polyline reference changes (new generation), allow a new fit.
  useEffect(() => {
    didFitOnceRef.current = false;
  }, [polyline]);

  // ---- Rejoin polyline updates ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyRouteData(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejoinPolyline, mapReady]);

  // ---- CANVAS 2D OVERLAY — Plan B polyline renderer per user directive.
  //      Instead of relying only on MapLibre's WebGL geojson layer (which
  //      failed to paint the line on the user's iPhone despite valid
  //      data + moveLayer + triggerRepaint), we draw the polyline on a
  //      2D canvas positioned ABOVE the map. This bypasses every WebGL
  //      layer race, every source-timing quirk, every layer-order bug.
  //      The canvas is redrawn on every map render tick, so it stays
  //      pixel-perfect during pan/zoom/rotation. ----
  const overlayRef = useRef(null);
  const drawCanvasOverlayRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    const canvas = overlayRef.current;
    if (!map || !canvas) return;

    const draw = () => {
      try {
        const container = map.getContainer();
        const w = container.clientWidth;
        const h = container.clientHeight;
        const dpr = window.devicePixelRatio || 1;
        if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
          canvas.width = w * dpr;
          canvas.height = h * dpr;
          canvas.style.width = w + "px";
          canvas.style.height = h + "px";
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        const pl = polylineRef.current;
        if (!Array.isArray(pl) || pl.length < 2) return;

        // Project each polyline vertex (lat,lng → screen px). Skip
        // entries that aren't valid [lat,lng] arrays, so a malformed
        // backend response never crashes the canvas.
        const pts = [];
        for (let i = 0; i < pl.length; i++) {
          const p = pl[i];
          if (!Array.isArray(p) || p.length < 2) continue;
          const lat = Number(p[0]);
          const lng = Number(p[1]);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
          const s = map.project([lng, lat]);
          if (!s || !Number.isFinite(s.x) || !Number.isFinite(s.y)) continue;
          pts.push([s.x, s.y]);
        }
        if (pts.length < 2) return;

        // Layer 1: black shadow, wide, high-contrast.
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 12;
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
        ctx.stroke();

        // Layer 2: fluorescent yellow rally line.
        ctx.strokeStyle = "#FFEE00";
        ctx.lineWidth = 8;
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
        ctx.stroke();

        // Rejoin overlay (dashed) if present.
        const rj = rejoinRef.current;
        if (Array.isArray(rj) && rj.length >= 2) {
          const rpts = [];
          for (let i = 0; i < rj.length; i++) {
            const p = rj[i];
            if (!Array.isArray(p) || p.length < 2) continue;
            const lat = Number(p[0]);
            const lng = Number(p[1]);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            const s = map.project([lng, lat]);
            if (!s) continue;
            rpts.push([s.x, s.y]);
          }
          if (rpts.length >= 2) {
            // Phase 5.3: NO dashed style on the rejoin overlay either.
            // Draw a solid yellow line identical to the main route so the
            // rider never sees the "line style flickering" bug.
            ctx.strokeStyle = "#000000";
            ctx.lineWidth = 12;
            ctx.beginPath();
            ctx.moveTo(rpts[0][0], rpts[0][1]);
            for (let i = 1; i < rpts.length; i++) ctx.lineTo(rpts[i][0], rpts[i][1]);
            ctx.stroke();
            ctx.strokeStyle = "#FFEE00";
            ctx.lineWidth = 8;
            ctx.beginPath();
            ctx.moveTo(rpts[0][0], rpts[0][1]);
            for (let i = 1; i < rpts.length; i++) ctx.lineTo(rpts[i][0], rpts[i][1]);
            ctx.stroke();
          }
        }
      } catch (err) {
        // Never let a canvas draw error crash the whole app.
        // eslint-disable-next-line no-console
        console.error("[MapView] canvas overlay draw error:", err);
      }
    };

    drawCanvasOverlayRef.current = draw;
    // Bind to MapLibre camera lifecycle events. `move` fires on every
    // pan/zoom/rotate frame; `render` catches everything else. Both are
    // cheap because our draw() is O(n) in polyline length.
    map.on("move", draw);
    map.on("moveend", draw);
    map.on("zoom", draw);
    map.on("rotate", draw);
    map.on("render", draw);
    map.on("resize", draw);
    draw();
    return () => {
      map.off("move", draw);
      map.off("moveend", draw);
      map.off("zoom", draw);
      map.off("rotate", draw);
      map.off("render", draw);
      map.off("resize", draw);
    };
  }, [mapReady]);

  // Redraw the canvas overlay when the polyline reference changes.
  useEffect(() => {
    drawCanvasOverlayRef.current?.();
  }, [polyline, rejoinPolyline, mapReady]);

  // ---- Start marker ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!start) {
      startMarkerRef.current?.remove();
      startMarkerRef.current = null;
      return;
    }
    if (!startMarkerRef.current) {
      startMarkerRef.current = new maplibregl.Marker({ element: startMarkerEl() })
        .setLngLat([start.lng, start.lat])
        .addTo(map);
    } else {
      startMarkerRef.current.setLngLat([start.lng, start.lat]);
    }
  }, [start, mapReady]);

  // ---- Turn point (A-R midpoint) marker ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    turnMarkerRef.current?.remove();
    turnMarkerRef.current = null;
    if (turnPoint && turnPoint.lat != null && turnPoint.lng != null) {
      turnMarkerRef.current = new maplibregl.Marker({
        element: turnMarkerEl(turnPoint.label),
        anchor: "bottom",
      })
        .setLngLat([turnPoint.lng, turnPoint.lat])
        .addTo(map);
    }
  }, [turnPoint, mapReady]);

  // ---- Weather markers: REMOVED per user directive after road test.
  //      Temperature pills cluttered the map view; if we want to re-add
  //      them in the future, do it inside the elevation profile card,
  //      NOT on the map surface. ----

  // ---- Live arrow marker: create once, then only update position/rotation
  //      (avoids marker teardown every GPS tick = smooth animation).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (live) {
      if (!liveMarkerRef.current) {
        liveMarkerRef.current = new maplibregl.Marker({ element: liveMarkerEl(), rotationAlignment: "map" })
          .setLngLat([live.lng, live.lat])
          .addTo(map);
      } else {
        liveMarkerRef.current.setLngLat([live.lng, live.lat]);
      }
      // Rotate arrow (heading in degrees).
      //   When compass mode is ON, the MAP is rotated to align the direction
      //   of travel with "up" on screen — so the arrow must NOT be rotated
      //   in screen-space (it would double-rotate). We use heading=0.
      //   When compass mode is OFF, the map is north-up, so the arrow must
      //   rotate by snapHeading to point in the direction of travel.
      const rawHeading =
        snapHeading != null && !Number.isNaN(snapHeading) ? snapHeading : live.heading;
      const heading = compassMode && rawHeading != null ? 0 : rawHeading;
      const el = liveMarkerRef.current.getElement();
      const arrow = el.querySelector(".brema-live-arrow");
      if (arrow && heading != null && !Number.isNaN(heading)) {
        arrow.style.transform = `rotate(${heading}deg)`;
      } else if (arrow) {
        arrow.style.transform = "rotate(0deg)";
      }
      // Night visibility boost DISABLED — night mode itself is turned off,
      // arrow stays a fixed 1.0 scale.
      const scale = 1;
      el.style.setProperty("--brema-live-scale", String(scale));
    } else {
      liveMarkerRef.current?.remove();
      liveMarkerRef.current = null;
    }
  }, [live, snapHeading, mapReady, compassMode]);

  // ---- Follow user: fixed zoom (16) + easeTo pan / jumpTo when far ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !followUser || !live) return;

    // Fixed zoom level per user directive after road test: dynamic zoom
    // felt "stretched too tight" and disorienting. Zoom 16 shows ~200m
    // in each direction — enough road ahead for a moto rider without
    // losing detail on turns. Only zoom in slightly (17) when a turn is
    // imminent (<150m).
    let zoom = 16;
    if (nearTurn && nearTurn.distanceKm * 1000 < 150) zoom = 17;

    // Compass mode: rotate map to snapped heading so "up" = direction of
    // travel. Otherwise keep north-up.
    const bearing =
      compassMode && snapHeading != null && !Number.isNaN(snapHeading)
        ? snapHeading
        : 0;

    // Push the vehicle up out of the bottom UI panel (elevation strip +
    // stats bar occupy the lower ~220px of the viewport). Padding-bottom
    // 220 makes MapLibre place the centred point ABOVE that panel, so
    // the arrow sits at roughly the middle of the visible map area with
    // plenty of road ahead.
    const padding = { top: 30, bottom: 220, left: 20, right: 20 };

    // Distance-adaptive follow: if the vehicle has moved more than ~150m
    // since the last camera update (or the previous easeTo hasn't
    // finished before the next GPS tick arrives), do an instant jumpTo
    // so the marker never trails off-screen during a fast highway run.
    // Otherwise the smooth easeTo gives the buttery pan feel the rider
    // expects. This directly fixes "the GPS doesn't follow the vehicle"
    // on faster mopeds / e-bikes where the tick rate outpaces the
    // animation.
    let distToCurrentCenter = 0;
    try {
      const c = map.getCenter();
      const dLat = (live.lat - c.lat) * 111320;
      const dLng = (live.lng - c.lng) * 111320 * Math.cos((live.lat * Math.PI) / 180);
      distToCurrentCenter = Math.sqrt(dLat * dLat + dLng * dLng);
    } catch { /* ignore */ }

    if (distToCurrentCenter > 150) {
      map.jumpTo({
        center: [live.lng, live.lat],
        zoom,
        bearing,
        padding,
      });
    } else {
      map.easeTo({
        center: [live.lng, live.lat],
        zoom,
        bearing,
        padding,
        duration: 400,
        essential: true,
      });
    }
  }, [live, followUser, mapReady, nearTurn, compassMode, snapHeading]);

  // Reset padding when follow-user turns off (otherwise the preview is
  // permanently pushed off-center).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!followUser) {
      map.easeTo({
        padding: { top: 20, bottom: 20, left: 20, right: 20 },
        bearing: 0,
        duration: 400,
      });
    }
  }, [followUser, mapReady]);

  return (
    <div
      className="relative h-full w-full"
      data-testid="brema-map"
      style={{ touchAction: "none", overscrollBehavior: "contain" }}
    >
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{ background: "#131822", width: "100%", height: "100%", minHeight: "300px", touchAction: "none" }}
      />

      {/* CANVAS 2D POLYLINE OVERLAY (Plan B renderer, always visible).
          Positioned above the map raster/vector tiles at z-10, below any
          UI overlays (turn banner z-500, debug badge z-9990). Uses
          pointer-events-none so it doesn't intercept map gestures. */}
      <canvas
        ref={overlayRef}
        data-testid="polyline-canvas-overlay"
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 10 }}
      />

      {/* Zoom +/- buttons — restored per user directive after road test.
          Pinch-to-zoom while riding is fiddly, dedicated buttons are far
          more usable. Positioned top-right, below the view-toggle bar. */}
      <div
        data-testid="zoom-controls"
        className="absolute top-2 right-2 z-[520] flex flex-col gap-1"
        style={{ marginTop: followUser ? "60px" : "48px" }}
      >
        <button
          data-testid="zoom-in-btn"
          onClick={() => {
            const map = mapRef.current;
            if (map) map.zoomIn({ duration: 300 });
          }}
          className="w-11 h-11 bg-brema-obsidian/90 border border-brema-yellow/40 text-brema-yellow font-bold text-xl backdrop-blur shadow-2xl active:scale-95 transition-transform"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          data-testid="zoom-out-btn"
          onClick={() => {
            const map = mapRef.current;
            if (map) map.zoomOut({ duration: 300 });
          }}
          className="w-11 h-11 bg-brema-obsidian/90 border border-brema-yellow/40 text-brema-yellow font-bold text-xl backdrop-blur shadow-2xl active:scale-95 transition-transform"
          aria-label="Zoom out"
        >
          −
        </button>
      </div>

      {/* DEBUG BADGE — shown when the URL has ?debug=1. Reports the exact
          polyline stats we handed the renderers so we can catch coord-
          inversion / empty-payload bugs. DEFENSIVE against malformed
          items to avoid the .toFixed-on-undefined crash. */}
      {(typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1") && (() => {
        const len = Array.isArray(polyline) ? polyline.length : 0;
        const safeCoord = (p) => {
          if (!Array.isArray(p) || p.length < 2) return "?,?";
          const a = Number(p[0]);
          const b = Number(p[1]);
          return `${Number.isFinite(a) ? a.toFixed(4) : "?"}, ${Number.isFinite(b) ? b.toFixed(4) : "?"}`;
        };
        return (
          <div
            data-testid="mapview-debug-badge"
            className="absolute top-2 left-2 z-[9990] bg-brema-red text-white font-mono text-[10px] leading-tight px-2 py-1 pointer-events-none max-w-[75%] break-all"
          >
            <div>PL len: {len}</div>
            {len > 0 && (
              <>
                <div>first: [{safeCoord(polyline[0])}]</div>
                <div>last: [{safeCoord(polyline[len - 1])}]</div>
                <div>mapReady: {String(mapReady)}</div>
              </>
            )}
          </div>
        );
      })()}

      {/* Giant turn indicator overlay — visible only near a fork/turn */}
      {nearTurn && live && <TurnIndicator turn={nearTurn} scale={1} />}

      {/* Night-mode pill removed per user directive: night mode tile swap
          disabled entirely, so no need to signal it. */}

      {/* Inline styles for the live marker (pulse + scale variable) */}
      <style>{`
        .brema-live-marker { position: relative; width: 44px; height: 44px; pointer-events: none; --brema-live-scale: 1; transform: scale(var(--brema-live-scale)); transform-origin: center center; }
        .brema-live-pulse { position: absolute; inset: 0; border-radius: 999px; background: radial-gradient(circle, rgba(193,39,45,0.35) 0%, rgba(193,39,45,0) 70%); animation: brema-pulse 1.6s ease-out infinite; }
        .brema-live-arrow { position: absolute; top: 50%; left: 50%; width: 26px; height: 26px; margin: -13px 0 0 -13px; transition: transform 0.35s cubic-bezier(.25,.8,.25,1); }
        @keyframes brema-pulse { 0% { transform: scale(0.6); opacity: 1; } 100% { transform: scale(1.8); opacity: 0; } }
        .maplibregl-ctrl-attrib.maplibregl-compact { background: rgba(19,24,34,0.75) !important; color: #eee !important; }
        .maplibregl-ctrl-attrib a { color: #FFD400 !important; }
      `}</style>
    </div>
  );
}

function TurnIndicator({ turn, scale = 1 }) {
  const dist = Math.max(0, Math.round(turn.distanceKm * 1000));
  const sharp = Math.abs(turn.delta) > 90;
  const bg = sharp ? "#C1272D" : "#FFD400";
  const fg = sharp ? "#FFFFFF" : "#131822";
  const label =
    turn.direction === "u-turn"
      ? "INVERSIONE A U"
      : turn.direction === "left"
      ? "SVOLTA A SINISTRA"
      : "SVOLTA A DESTRA";
  const rotationDeg =
    turn.direction === "u-turn" ? 180 : turn.direction === "left" ? -90 : 90;
  const iconSize = Math.round(42 * scale);
  return (
    <div
      data-testid="turn-indicator"
      className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 z-[500] border-4 shadow-2xl flex items-center gap-3"
      style={{
        background: bg,
        borderColor: fg,
        color: fg,
        padding: `${Math.round(12 * scale)}px ${Math.round(16 * scale)}px`,
        transform: `translateX(-50%) scale(${scale})`,
        transformOrigin: "top center",
      }}
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" style={{ transform: `rotate(${rotationDeg}deg)` }}>
        <path d="M12 2 L20 12 L15 12 L15 22 L9 22 L9 12 L4 12 Z" fill={fg} stroke="none" />
      </svg>
      <div className="text-left">
        <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">Tra {dist} m</div>
        <div className="font-display font-black text-lg uppercase leading-none">{label}</div>
      </div>
    </div>
  );
}
